/**
 * 0~100 진행률 막대. 끝에 깃발, 진행 지점에 캐릭터 이미지를 얹는다.
 *
 * 홈의 ProgressCard 뿐 아니라 pages/problem, pages/poc/dq-essay-quiz 에서도 직접 쓰인다.
 * home 도메인 폴더에 있지만 실질적으로 여러 화면이 공유하는 컴포넌트다.
 */
import React from "react";
import Flag from "../icons/home/Flag";
import Image from "next/image";

interface ProgressBarProps {
  color?: string;
  progress: number; // 진행 퍼센트 (0에서 100까지)
  /** 진행 인디케이터로 띄울 캐릭터 이미지 경로. 미지정 시 기본 codingBuddy. */
  characterSrc?: string;
  /**
   * 체크포인트 구간 수. 점은 steps + 1개(시작점 포함, 마지막 점은 깃발 자리).
   * 논서술형은 미션 수(6)를 넘겨 "미션 하나 완료 = 점 하나"가 되게 한다. 기본 5(객관식 기존 표시).
   */
  steps?: number;
}

/** 진행률 막대. progress 값에 맞춰 채움 길이·캐릭터 위치·체크포인트 색을 계산한다. */
const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  color = "gray",
  characterSrc = "/images/codingBuddy.png",
  steps = 5,
}) => {
  const safeSteps = Math.max(1, Math.round(steps));
  // 시작점(0%)부터 마지막 점 직전까지. 마지막 점(100%)은 아래 left-full 원이 맡는다.
  const checkpoints = Array.from({ length: safeSteps }, (_, i) => (i / safeSteps) * 100);
  return (
    <div className="relative pt-[40px]">
      <div
        className={`relative w-full h-[8px] rounded-full ${
          color === "gray" ? " bg-fill-strong " : "bg-white"
        }`}
      >
        <div
          className="absolute top-0 h-[8px] bg-primary-300 rounded-full"
          style={{ width: `${progress}%` }}
        ></div>

        {/* 진행률에 따라 Image 위치를 동적으로 설정 */}
        <Image
          src={characterSrc}
          alt=""
          width={37}
          height={41}
          className="absolute bottom-[7px] z-40" // -18.5px
          style={{ left: `calc(${progress}% - 14px)` }} // 이미지가 바의 끝에 맞도록 배치
        />

        {/* 진행률을 steps 등분한 체크포인트(시작점 포함)로 쪼개 각 지점을 지났는지 색으로 표시한다.
            100%는 아래 progress-circle left-full 이 따로 맡는다.
            시작점(0%)은 조금이라도 진행했으면 칠하고, 나머지는 그 지점에 도달했을 때 칠한다.
            비교는 반올림 오차(1/6 = 16.67% 등) 때문에 0.5%p 여유를 둔다. */}
        {checkpoints.map((left, i) => (
          <div
            key={left}
            className={`progress-circle ${
              (i === 0 ? progress > 0 : progress + 0.5 >= left)
                ? "bg-primary"
                : "bg-disabled"
            }`}
            style={{ left: `${left}%` }}
          ></div>
        ))}
        <div
          className={`progress-circle left-full ${
            progress == 100 ? "bg-primary" : "bg-disabled"
          }`}
        ></div>
        <div className="absolute -top-6 left-full">
          <Flag />
        </div>
      </div>

      <div className="text-14 text-body font-medium pt-[9px]">
        {progress === 100 ? "문제 풀이 완료!" : `진행률 ${progress}%`}
      </div>
    </div>
  );
};
export default ProgressBar;
